import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './SystemDashboard.css';
import { useAuth } from '../../hooks/useAuth';
import { ArrowLeft } from 'lucide-react';
import OverviewTab from './components/OverviewTab';
import UsersTab from './components/UsersTab';
import SocialTab from './components/SocialTab';
import MedicalTab from './components/MedicalTab';
import DashboardFilters from './components/DashboardFilters';

// Import Detailed Components
import DetailedOverviewPage from './pages/DetailedOverviewPage';
import DetailedUsersPage from './pages/DetailedUsersPage';
import DetailedSocialPage from './pages/DetailedSocialPage';
import DetailedMedicalPage from './pages/DetailedMedicalPage';

/**
 * SystemDashboard
 */
const SystemDashboard = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/auth/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // URL-based state
    const activeTab = searchParams.get('tab') || 'overview';
    const viewMode = searchParams.get('view') || 'summary';

    // Derive filters directly from URL (Single Source of Truth)
    const filters = {
        timeRange: searchParams.get('timeRange') || '30days',
        category: searchParams.get('category') || 'all',
        start_date: searchParams.get('start_date') || '',
        end_date: searchParams.get('end_date') || '',
        gender: searchParams.get('gender') || '',
        min_age: searchParams.get('min_age') || '',
        max_age: searchParams.get('max_age') || '',
        min_reactions: searchParams.get('min_reactions') || '',
        min_comments: searchParams.get('min_comments') || '',
        visit_type: searchParams.get('visit_type') || '',
        country: searchParams.get('country') || ''
    };

    const setActiveTab = (tab) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', tab);
        newParams.set('view', 'summary');
        setSearchParams(newParams);
    };

    const setViewMode = (view) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('view', view);
        setSearchParams(newParams);
    };

    const categoryConfig = {
        users: {
            show: true,
            label: 'User Type',
            options: [
                { value: 'PET_GUARDIAN', label: 'Pet Guardians' },
                { value: 'VETERINARIAN', label: 'Veterinarians' },
                { value: 'SERVICE_PROVIDER', label: 'Service Providers' },
                { value: 'ADMIN', label: 'Administrators' },
                { value: 'ORGANIZATION_ADMIN', label: 'Organization Admins' }
            ]
        },
        social: {
            show: true,
            label: 'Post Type',
            options: [
                { value: 'regular', label: 'Regular Posts' },
                { value: 'adoption', label: 'Adoption Posts' },
                { value: 'channel', label: 'Channel Posts' },
                { value: 'marketplace', label: 'Marketplace Posts' }
            ]
        },
        medical: {
            show: true,
            label: 'Appointment Status',
            options: [
                { value: 'pending', label: 'Pending' },
                { value: 'confirmed', label: 'Confirmed' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' }
            ]
        },
        overview: {
            show: false
        }
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: '📊' },
        { id: 'users', label: 'Users', icon: '👥' },
        { id: 'social', label: 'Social', icon: '💬' },
        { id: 'medical', label: 'Medical', icon: '🏥' }
    ];

    const handleFilterChange = (newFilters) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(newFilters).forEach(([key, value]) => {
            if (value && value !== 'all') {
                newParams.set(key, value);
            } else {
                newParams.delete(key);
            }
        });
        setSearchParams(newParams);
    };

    return (
        <div className="dashboard-container">
            <div className="dashboard-content">
                {/* Sticky Header Section */}
                <div className="dashboard-header-sticky">
                    {/* Top nav bar with back and logout buttons */}
                    <div className="dashboard-nav-bar">
                        <button
                            className="dashboard-nav-btn dashboard-nav-btn--back"
                            onClick={() => navigate('/admin/dashboard')}
                            title="Back to Admin Dashboard"
                        >
                            <ArrowLeft size={16} />
                            <span>Admin Dashboard</span>
                        </button>
                        <button
                            className="dashboard-nav-btn dashboard-nav-btn--logout"
                            onClick={handleLogout}
                            title="Logout"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                <polyline points="16 17 21 12 16 7" />
                                <line x1="21" y1="12" x2="9" y2="12" />
                            </svg>
                            <span>Logout</span>
                        </button>
                    </div>

                    {viewMode === 'summary' ? (
                        <div className="dashboard-header">
                            <h1 className="header-title">System Statistics</h1>
                            <p className="header-subtitle">Real-time analytics and insights</p>
                        </div>
                    ) : (
                        <div className="dashboard-header-mini">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => setViewMode('summary')}
                                    className="p-2 hover:bg-white/20 rounded-xl transition-colors text-white group"
                                    title="Back to Dashboard"
                                >
                                    <ArrowLeft size={24} className="group-hover:-translate-x-1 transition-transform" />
                                </button>
                                <div>
                                    <h1 className="text-xl md:text-2xl font-bold text-white leading-tight">
                                        {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Analytics
                                    </h1>
                                    <p className="text-xs md:text-sm text-indigo-100 opacity-90">Detailed statistics and insights</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-4 pb-2 border-b border-gray-100/50">
                        <DashboardFilters
                            currentFilters={filters}
                            onFilterChange={handleFilterChange}
                            showCategoryFilter={categoryConfig[activeTab]?.show}
                            categoryLabel={categoryConfig[activeTab]?.label}
                            categories={categoryConfig[activeTab]?.options}
                            activeTab={activeTab}
                        />
                    </div>
                </div>

                {/* Toolbar Section (Tabs) - Only show in summary mode */}
                {viewMode === 'summary' && (
                    <div className="dashboard-toolbar">
                        <div className="tabs-section">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                                >
                                    <span>{tab.icon}</span> {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Content Area */}
                <div className="tab-content">
                    {viewMode === 'summary' ? (
                        <>
                            {activeTab === 'overview' && (
                                <OverviewTab
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                            {activeTab === 'users' && (
                                <UsersTab
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                            {activeTab === 'social' && (
                                <SocialTab
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                            {activeTab === 'medical' && (
                                <MedicalTab
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            {activeTab === 'overview' && (
                                <DetailedOverviewPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                            {activeTab === 'users' && (
                                <DetailedUsersPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                            {activeTab === 'social' && (
                                <DetailedSocialPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                            {activeTab === 'medical' && (
                                <DetailedMedicalPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SystemDashboard;
